import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ROLE_LABEL, useAuth } from '../auth';
import type { Role } from '../types';
import { versionText } from '../version';
import { VersionPopup } from './VersionPopup';
import { GlobalSearch } from './GlobalSearch';

interface NavItem {
  to?: string;
  label: string;
  roles?: Role[];
  soon?: boolean;
  /**
   * Bereich, der die Sichtbarkeit dieses Punktes steuert, falls er vom Bereich
   * der Gruppe abweicht (z. B. Standtage steht unter Abrechnung, hängt aber am
   * Lager-Recht). Ohne Angabe gilt der Bereich der Gruppe.
   */
  bereich?: string;
  /**
   * Für Auftraggeber sichtbar. Alles ohne dieses Kennzeichen bleibt den
   * Mitarbeitern des Dienstleisters vorbehalten - der Server setzt dieselbe
   * Grenze noch einmal durch, die Oberfläche allein wäre keine Sicherung.
   */
  portal?: boolean;
  /** Abweichende Beschriftung im Auftraggeber-Portal. */
  portalLabel?: string;
}

interface NavGroup {
  title: string;
  /** Inhaltsbereich der Gruppe - steuert die konfigurierbare Rollen-Sichtbarkeit. */
  bereich: string;
  items: NavItem[];
}

/**
 * Die Menüführung bildet bewusst schon den Zielumfang ab. Was noch nicht
 * gebaut ist, steht als "geplant" da - so sieht man beim Durchklicken, wohin
 * das System wächst, ohne auf leere Masken zu stossen.
 */
export const NAV: NavGroup[] = [
  {
    title: 'Übersicht',
    bereich: 'uebersicht',
    items: [
      { to: '/', label: 'Startseite' },
      { to: '/dashboard', label: 'Kennzahlen' },
    ],
  },
  {
    title: 'Lager',
    bereich: 'lager',
    items: [
      { to: '/bestand', label: 'Bestand' },
      {
        to: '/meldungen',
        label: 'Mindestbestand',
        roles: ['admin', 'kaufmann', 'disponent', 'schichtleiter'],
      },
      { to: '/wareneingang', label: 'Wareneingang' },
      {
        to: '/kommissionierung',
        label: 'Kommissionierung',
        roles: ['admin', 'schichtleiter', 'lagerist', 'disponent'],
      },
      {
        to: '/packtisch',
        label: 'Packtisch',
        roles: ['admin', 'schichtleiter', 'lagerist', 'disponent'],
      },
      {
        to: '/nachschub',
        label: 'Nachschub',
        roles: ['admin', 'schichtleiter', 'lagerist', 'disponent', 'kaufmann'],
      },
      {
        to: '/rueckverfolgung',
        label: 'Rückverfolgung',
        roles: ['admin', 'schichtleiter', 'lagerist', 'disponent', 'kaufmann'],
      },
      { to: '/buchen', label: 'Buchen', roles: ['admin', 'schichtleiter', 'lagerist', 'disponent'] },
      { to: '/bewegungen', label: 'Bewegungen' },
      { to: '/container', label: 'Container' },
      { to: '/inventur', label: 'Inventur' },
    ],
  },
  {
    title: 'Stammdaten',
    bereich: 'stammdaten',
    items: [
      { to: '/artikel', label: 'Artikel' },
      { to: '/warengruppen', label: 'Warengruppen' },
      { to: '/partner', label: 'Partner' },
      { to: '/lagerstruktur', label: 'Lagerstruktur' },
      { to: '/etiketten', label: 'Etikettendruck' },
      { to: '/import', label: 'Datenimport', roles: ['admin', 'kaufmann', 'disponent'] },
    ],
  },
  {
    title: 'Einkauf',
    bereich: 'einkauf',
    items: [
      { to: '/bestellungen', label: 'Bestellungen' },
      {
        to: '/rechnungseingang',
        label: 'Rechnungseingang',
        roles: ['admin', 'kaufmann', 'disponent'],
      },
    ],
  },
  {
    title: 'Verkauf',
    bereich: 'verkauf',
    items: [
      { to: '/kasse', label: 'Kasse' },
      { to: '/kassenabschluss', label: 'Kassenabschluss', roles: ['admin', 'kaufmann', 'schichtleiter'] },
      {
        to: '/kassenpersonal',
        label: 'Kassenpersonal',
        roles: ['admin', 'kaufmann', 'schichtleiter'],
      },
      {
        to: '/bestellen',
        label: 'Artikel anfordern',
        roles: ['auftraggeber'],
        portal: true,
        portalLabel: 'Artikel anfordern',
      },
      { to: '/auftraege', label: 'Aufträge & Angebote' },
      {
        to: '/warenrechnung',
        label: 'Warenrechnung',
        roles: ['admin', 'kaufmann', 'disponent'],
      },
      {
        to: '/verkaufspreise',
        label: 'Verkaufspreise',
        roles: ['admin', 'kaufmann', 'disponent'],
      },
    ],
  },
  {
    title: 'Versand',
    bereich: 'versand',
    items: [
      { to: '/retouren', label: 'Retouren' },
      {
        to: '/ladungstraeger',
        label: 'Ladungsträger',
        roles: ['admin', 'kaufmann', 'disponent', 'schichtleiter', 'lagerist'],
      },
    ],
  },
  {
    title: 'Abrechnung',
    bereich: 'abrechnung',
    items: [
      {
        to: '/abrechnung',
        label: 'Lagergeld & Rechnungen',
        roles: ['admin', 'kaufmann', 'disponent'],
      },
      {
        to: '/standtage',
        label: 'Standtage & Lagergeld',
        roles: ['admin', 'kaufmann', 'disponent', 'schichtleiter'],
        bereich: 'lager',
      },
      { to: '/offene-posten', label: 'Offene Posten', roles: ['admin', 'kaufmann'] },
      { to: '/bank', label: 'Bankabgleich', roles: ['admin', 'kaufmann'] },
      { to: '/zahllauf', label: 'SEPA-Zahllauf', roles: ['admin', 'kaufmann'] },
      { to: '/buchhaltung', label: 'DATEV & E-Rechnung', roles: ['admin', 'kaufmann'] },
      { to: '/mailversand', label: 'Belegversand', roles: ['admin', 'kaufmann'] },
    ],
  },
  {
    title: 'Auswertungen',
    bereich: 'auswertungen',
    items: [
      { to: '/chef', label: 'Geschäftsführung', roles: ['admin', 'kaufmann'] },
      { to: '/lagerwert', label: 'Warenwert & Rohertrag', roles: ['admin', 'kaufmann'] },
      {
        to: '/kalkulation',
        label: 'Handelsspanne',
        roles: ['admin', 'kaufmann', 'disponent'],
      },
      {
        to: '/avu',
        label: 'Abverkaufsunterstützung',
        roles: ['admin', 'kaufmann', 'disponent'],
      },
      {
        to: '/durchlaufzeiten',
        label: 'Durchlaufzeiten',
        roles: ['admin', 'kaufmann', 'disponent', 'schichtleiter'],
      },
    ],
  },
  {
    title: 'Steuer & Prüfung',
    bereich: 'steuer',
    items: [
      { to: '/betriebspruefung', label: 'Betriebsprüfung', roles: ['admin', 'kaufmann'] },
      { to: '/aufbewahrung', label: 'Aufbewahrung & Löschung', roles: ['admin', 'kaufmann'] },
      {
        to: '/verfahrensdokumentation',
        label: 'Verfahrensdokumentation',
        roles: ['admin', 'kaufmann'],
      },
    ],
  },
  {
    title: 'Verwaltung',
    bereich: 'verwaltung',
    items: [
      // Ein Einstieg statt vieler Einzelpunkte: das GF/Admin-Cockpit bündelt
      // Benutzer, Rollen, Fonds, Module, Bridge, System und Protokoll. Die
      // Einzelseiten bleiben als Routen erhalten und werden von dort verlinkt.
      // bereich-Override 'stammdaten', damit auch der Disponent (hat kein
      // 'verwaltung') den Eintrag wegen der Fonds-Pflege sieht; die Kacheln
      // selbst sind je Rolle gefiltert.
      {
        to: '/verwaltung',
        label: 'GF / Admin-Cockpit',
        roles: ['admin', 'kaufmann', 'disponent'],
        bereich: 'stammdaten',
      },
    ],
  },
];

// Seiten, die nur noch über das GF/Admin-Cockpit erreichbar sind (nicht mehr
// als eigener Menüpunkt) - damit die Kopfzeile dort trotzdem den Titel zeigt.
const COCKPIT_TITEL: Record<string, string> = {
  '/benutzer': 'Benutzer',
  '/rollen': 'Rollen & Rechte',
  '/fonds': 'Fonds (Abteilungen)',
  '/module': 'Module & Betriebsart',
  '/bridges': 'Offline-Bridge',
  '/system': 'System & Datensicherung',
  '/protokoll': 'Protokoll',
};

function currentTitle(pathname: string): string {
  if (pathname.startsWith('/neuigkeiten')) return 'Neu in dieser Version';
  for (const group of NAV) {
    for (const item of group.items) {
      if (item.to && (item.to === pathname || (item.to !== '/' && pathname.startsWith(item.to)))) {
        return item.label;
      }
    }
  }
  if (COCKPIT_TITEL[pathname]) return COCKPIT_TITEL[pathname];
  return 'Logistik-WaWi';
}

export function Layout() {
  const { user, owners, ownerId, setOwnerId, logout, can, verborgen, bereiche, menuVersteckt } = useAuth();
  const location = useLocation();
  const isPortal = user?.role === 'auftraggeber';
  const lockedToOwner = Boolean(user?.ownerId);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-zeile">
            <img className="brand-logo" src="/kg-media.svg" alt="" />
            Logistik-WaWi
          </span>
          <small>{isPortal ? 'Auftraggeber-Portal' : 'Warenwirtschaft & Lager'}</small>
        </div>

        <nav className="nav">
          {NAV.map((group) => {
            const visible = group.items.filter((item) =>
              (!item.to || !verborgen.has(item.to)) &&
              (!item.to || !menuVersteckt.has(item.to)) &&
              (isPortal
                ? item.portal
                : bereiche.has(item.bereich ?? group.bereich) && (!item.roles || can(...item.roles))),
            );
            if (visible.length === 0) return null;

            return (
              <div key={group.title}>
                <div className="nav-group">{group.title}</div>
                {visible.map((item) =>
                  item.to ? (
                    <NavLink key={item.label} to={item.to} end={item.to === '/'}>
                      {(isPortal && item.portalLabel) || item.label}
                    </NavLink>
                  ) : (
                    <span key={item.label} className="disabled">
                      {item.label}
                      <span className="soon">geplant</span>
                    </span>
                  ),
                )}
              </div>
            );
          })}
        </nav>

        {!isPortal && (
          <div className="sidebar-fuss">
            <NavLink to="/neuigkeiten" className="neu-link">Neu in dieser Version</NavLink>
            <span className="version-stand">{versionText()}</span>
          </div>
        )}
      </aside>

      {user && !isPortal && <VersionPopup userId={user.id} />}

      <div className="main">
        <header className="topbar">
          <h1>{currentTitle(location.pathname)}</h1>
          {!isPortal && <GlobalSearch />}
          <span className="spacer" />

          {owners.length > 1 && !lockedToOwner && (
            <select
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              style={{ width: 220 }}
              title="Eigentümer der Ware"
            >
              <option value="">Alle Eigentümer</option>
              {owners.map((owner) => (
                <option key={owner.id} value={owner.id}>
                  {owner.name}
                </option>
              ))}
            </select>
          )}

          <div className="who">
            <strong>{user?.name}</strong>
            <span>{user ? ROLE_LABEL[user.role] : ''}</span>
          </div>
          <button className="small" onClick={logout}>
            Abmelden
          </button>
        </header>

        <main className="content">
          {isPortal && location.pathname !== '/bestellen' ? (
            <Navigate to="/bestellen" replace />
          ) : menuVersteckt.has(location.pathname) ? (
            <div className="card" style={{ maxWidth: 520 }}>
              <h3 style={{ marginTop: 0 }}>Nicht freigeschaltet</h3>
              <p className="small muted" style={{ marginBottom: 0 }}>
                Dieser Menüpunkt ist für deine Rolle ausgeblendet. Wende dich an die
                Geschäftsführung, wenn du Zugriff brauchst.
              </p>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}
